import { supabase } from '../lib/supabase';
import { Fixture, FixtureRegistration, FixtureWaitlist, CourseGuide } from '../types/fixtures.types';
import { Database } from '../types/database.types';

type FixtureRow = Database['public']['Tables']['fixtures']['Row'];
type FixtureRegistrationRow = Database['public']['Tables']['fixture_registrations']['Row'];
type FixtureWaitlistRow = Database['public']['Tables']['fixture_waitlist']['Row'];
type CourseGuideRow = Database['public']['Tables']['course_guides']['Row'];

// Helper functions to convert snake_case to camelCase
const convertFixture = (row: FixtureRow): Fixture => ({
  id: row?.id ?? '',
  title: row?.title ?? '',
  description: row?.description ?? null,
  courseName: row?.course_name ?? '',
  courseLocation: row?.course_location ?? '',
  scheduledDate: row?.scheduled_date ?? '',
  maxCapacity: row?.max_capacity ?? 0,
  currentRegistrations: row?.current_registrations ?? 0,
  status: row?.status ?? 'upcoming',
  createdAt: row?.created_at ?? '',
  updatedAt: row?.updated_at ?? '',
});

const convertRegistration = (row: FixtureRegistrationRow): FixtureRegistration => ({
  id: row?.id ?? '',
  fixtureId: row?.fixture_id ?? '',
  memberName: row?.member_name ?? '',
  memberEmail: row?.member_email ?? '',
  memberPhone: row?.member_phone ?? null,
  registrationStatus: row?.registration_status ?? 'pending_payment',
  paymentStatus: row?.payment_status ?? 'unpaid',
  paymentAmount: row?.payment_amount ?? 0,
  registrationDate: row?.registration_date ?? '',
  paymentDueDate: row?.payment_due_date ?? null,
  lastReminderSent: row?.last_reminder_sent ?? null,
  createdAt: row?.created_at ?? '',
  updatedAt: row?.updated_at ?? '',
});

const convertWaitlist = (row: FixtureWaitlistRow): FixtureWaitlist => ({
  id: row?.id ?? '',
  fixtureId: row?.fixture_id ?? '',
  memberName: row?.member_name ?? '',
  memberEmail: row?.member_email ?? '',
  memberPhone: row?.member_phone ?? null,
  position: row?.position ?? 0,
  notified: row?.notified ?? false,
  createdAt: row?.created_at ?? '',
});

const convertCourseGuide = (row: CourseGuideRow): CourseGuide => ({
  id: row?.id ?? '',
  fixtureId: row?.fixture_id ?? '',
  guideTitle: row?.guide_title ?? '',
  guideContent: row?.guide_content ?? '',
  courseTips: row?.course_tips ?? null,
  weatherConsiderations: row?.weather_considerations ?? null,
  dressCode: row?.dress_code ?? null,
  equipmentRecommendations: row?.equipment_recommendations ?? null,
  createdAt: row?.created_at ?? '',
});

export const fixtureService = {
  // Get all upcoming fixtures
  async getUpcomingFixtures(): Promise<Fixture[]> {
    try {
      const { data, error } = await supabase
        .from('fixtures')
        .select('*')
        .eq('status', 'upcoming')
        .gte('scheduled_date', new Date().toISOString())
        .order('scheduled_date', { ascending: true });

      if (error) throw error;
      return (data || []).map(convertFixture);
    } catch (error) {
      console.error('Error fetching fixtures:', error);
      return [];
    }
  },

  // Get fixture by ID with full details
  async getFixtureById(fixtureId: string): Promise<{ fixture: Fixture | null; guide: CourseGuide | null }> {
    try {
      const { data: fixtureData, error: fixtureError } = await supabase
        .from('fixtures')
        .select('*')
        .eq('id', fixtureId)
        .single();

      if (fixtureError) throw fixtureError;

      const { data: guideData } = await supabase
        .from('course_guides')
        .select('*')
        .eq('fixture_id', fixtureId)
        .single();

      return {
        fixture: fixtureData ? convertFixture(fixtureData) : null,
        guide: guideData ? convertCourseGuide(guideData) : null,
      };
    } catch (error) {
      console.error('Error fetching fixture details:', error);
      return { fixture: null, guide: null };
    }
  },

  // Register for a fixture
  async registerForFixture(
    fixtureId: string,
    memberName: string,
    memberEmail: string,
    memberPhone: string | null,
    paymentAmount: number
  ): Promise<{ success: boolean; registration: FixtureRegistration | null; waitlisted: boolean }> {
    try {
      // Check fixture capacity
      const { data: fixture } = await supabase
        .from('fixtures')
        .select('current_registrations, max_capacity')
        .eq('id', fixtureId)
        .single();

      if (!fixture) {
        throw new Error('Fixture not found');
      }

      const isFull = fixture?.current_registrations >= fixture?.max_capacity;

      if (isFull) {
        // Add to waitlist
        const { data: waitlistData, error: waitlistError } = await supabase
          .from('fixture_waitlist')
          .select('position')
          .eq('fixture_id', fixtureId)
          .order('position', { ascending: false })
          .limit(1);

        const nextPosition = waitlistData && waitlistData.length > 0 ? (waitlistData[0]?.position ?? 0) + 1 : 1;

        const { error: insertError } = await supabase.from('fixture_waitlist').insert({
          fixture_id: fixtureId,
          member_name: memberName,
          member_email: memberEmail,
          member_phone: memberPhone,
          position: nextPosition,
        });

        if (insertError) throw insertError;

        return { success: true, registration: null, waitlisted: true };
      }

      // Create registration
      const { data: registrationData, error: registrationError } = await supabase
        .from('fixture_registrations')
        .insert({
          fixture_id: fixtureId,
          member_name: memberName,
          member_email: memberEmail,
          member_phone: memberPhone,
          payment_amount: paymentAmount,
          registration_status: 'pending_payment',
          payment_status: 'unpaid',
        })
        .select()
        .single();

      if (registrationError) throw registrationError;

      // Send course guide email
      await this.sendCourseGuide(fixtureId, memberName, memberEmail);

      return {
        success: true,
        registration: registrationData ? convertRegistration(registrationData) : null,
        waitlisted: false,
      };
    } catch (error) {
      console.error('Error registering for fixture:', error);
      return { success: false, registration: null, waitlisted: false };
    }
  },

  // Send course guide email
  async sendCourseGuide(fixtureId: string, memberName: string, memberEmail: string): Promise<void> {
    try {
      const { fixture, guide } = await this.getFixtureById(fixtureId);

      if (!fixture || !guide) return;

      await supabase.functions.invoke('send-course-guide', {
        body: {
          memberName,
          memberEmail,
          fixtureName: fixture?.title,
          fixtureDate: fixture?.scheduledDate,
          courseName: fixture?.courseName,
          courseLocation: fixture?.courseLocation,
          guideTitle: guide?.guideTitle,
          guideContent: guide?.guideContent,
          courseTips: guide?.courseTips,
          weatherConsiderations: guide?.weatherConsiderations,
          dressCode: guide?.dressCode,
          equipmentRecommendations: guide?.equipmentRecommendations,
        },
      });

      // Log email
      await supabase.from('email_logs').insert({
        recipient_email: memberEmail,
        email_type: 'course_guide',
        fixture_id: fixtureId,
        status: 'sent',
      });
    } catch (error) {
      console.error('Error sending course guide:', error);
    }
  },

  // Send payment reminder
  async sendPaymentReminder(registrationId: string): Promise<void> {
    try {
      const { data: registration } = await supabase
        .from('fixture_registrations')
        .select('*, fixtures(*)')
        .eq('id', registrationId)
        .single();

      if (!registration || registration?.payment_status === 'paid') return;

      const daysRemaining = registration?.payment_due_date
        ? Math.ceil(
            (new Date(registration?.payment_due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
          )
        : 0;

      await supabase.functions.invoke('send-payment-reminder', {
        body: {
          memberName: registration?.member_name,
          memberEmail: registration?.member_email,
          fixtureName: registration?.fixtures?.title,
          fixtureDate: registration?.fixtures?.scheduled_date,
          courseName: registration?.fixtures?.course_name,
          paymentAmount: registration?.payment_amount,
          paymentDueDate: registration?.payment_due_date,
          daysRemaining,
        },
      });

      // Update last reminder sent
      await supabase
        .from('fixture_registrations')
        .update({ last_reminder_sent: new Date().toISOString() })
        .eq('id', registrationId);

      // Log email
      await supabase.from('email_logs').insert({
        recipient_email: registration?.member_email,
        email_type: 'payment_reminder',
        fixture_id: registration?.fixture_id,
        registration_id: registrationId,
        status: 'sent',
      });
    } catch (error) {
      console.error('Error sending payment reminder:', error);
    }
  },

  // Notify waitlist when spot opens
  async notifyWaitlist(fixtureId: string): Promise<void> {
    try {
      const { data: waitlistEntry } = await supabase
        .from('fixture_waitlist')
        .select('*, fixtures(*)')
        .eq('fixture_id', fixtureId)
        .eq('notified', false)
        .order('position', { ascending: true })
        .limit(1)
        .single();

      if (!waitlistEntry) return;

      await supabase.functions.invoke('send-waitlist-notification', {
        body: {
          memberName: waitlistEntry?.member_name,
          memberEmail: waitlistEntry?.member_email,
          fixtureName: waitlistEntry?.fixtures?.title,
          fixtureDate: waitlistEntry?.fixtures?.scheduled_date,
          courseName: waitlistEntry?.fixtures?.course_name,
          courseLocation: waitlistEntry?.fixtures?.course_location,
          waitlistPosition: waitlistEntry?.position,
        },
      });

      // Mark as notified
      await supabase.from('fixture_waitlist').update({ notified: true }).eq('id', waitlistEntry?.id);

      // Log email
      await supabase.from('email_logs').insert({
        recipient_email: waitlistEntry?.member_email,
        email_type: 'waitlist_notification',
        fixture_id: fixtureId,
        status: 'sent',
      });
    } catch (error) {
      console.error('Error notifying waitlist:', error);
    }
  },

  // Get registrations that need payment reminders
  async getRegistrationsNeedingReminders(): Promise<FixtureRegistration[]> {
    try {
      const threeDaysFromNow = new Date();
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

      const { data, error } = await supabase
        .from('fixture_registrations')
        .select('*')
        .eq('payment_status', 'unpaid')
        .lte('payment_due_date', threeDaysFromNow.toISOString())
        .or('last_reminder_sent.is.null,last_reminder_sent.lt.' + new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      if (error) throw error;
      return (data || []).map(convertRegistration);
    } catch (error) {
      console.error('Error fetching registrations needing reminders:', error);
      return [];
    }
  },

  // Get all fixture registrations
  async getAllRegistrations(): Promise<FixtureRegistration[]> {
    try {
      const { data, error } = await supabase
        .from('fixture_registrations')
        .select('*')
        .order('registration_date', { ascending: false });

      if (error) throw error;
      return (data || []).map(convertRegistration);
    } catch (error) {
      console.error('Error fetching all registrations:', error);
      return [];
    }
  },
};